import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { useAuthenticated } from '../../auth';
import { MicroPortalErrorFallback, MicroPortalLoadingFallback } from '../../components';
import { createMicroPortalUrl, createParams, getMicroPortalToken } from '../../util';

export const MicroPortalAuthenticate: FC = () => {
  const { app: appParam } = useParams();
  const [search] = useSearchParams();
  const { axios, account } = useAuthenticated();
  const [error, setError] = useState<boolean>(false);
  const [app, setApp] = useState<string>();
  const { t, i18n } = useTranslation(['common']);
  const devMode = search.get('devHost') || undefined;

  useEffect(() => {
    if (appParam) {
      setApp(appParam);
    }
  }, [appParam]);

  useEffect(() => {
    let mounted = true;

    if (!app) {
      return;
    }

    getMicroPortalToken(axios, app).then((auth) => {
      if (auth) {
        // Link launches arrive here with the caller's launch data (for
        // example, a Thing ID from a Thing-detail action). Keep that context
        // on the authenticated redirect; dropping it makes full-screen
        // launches fall back to an unscoped vehicle search.
        const launchData = Object.fromEntries(
          [...search.entries()].filter(([key]) => key !== 'devHost'),
        );
        const src = createMicroPortalUrl(
          app,
          createParams({ auth, domain: account.homeDomain, language: i18n.language, ...launchData }),
          devMode,
        );
        window.location.href = src;
      } else {
        setError(true);
      }
    }).catch(() => {
      if (!mounted) return;
      setError(true);
    }).finally(() => {
      if (!mounted) return;
    });

    return () => {
      mounted = false;
    };
  }, [t, axios, app, setError, account, devMode, i18n.language, search]);

  if (error) {
    return <><MicroPortalErrorFallback /></>;
  }

  return <MicroPortalLoadingFallback app={app || 'App'} />;
};
